<template>
  <v-container
    fluid
    style="max-width: 1400px; margin-left: 0;"
    :class="{ 'disable-all-buttons': isEditProhibited }"
    :style="containerHeightStyle"
  >
    <v-row v-if="!loading" class="fill-height pb-0">

      <!-- ── Columna principal ── -->
      <v-col
        ref="colContractContainer"
        :cols="mainColSize"
        class="d-flex flex-column fill-height pb-0"
        style="overflow-y: scroll; overflow-x: hidden;"
        :class="{ 'pt-0': isScrolled }"
        @scroll="onScroll"
      >
        <ProjectHeaderInfo
          v-if="contract"
          ref="contractHeaderInfo"
          :contract="contract"
          :project="project"
          :is_subcontract="isSubcontract"
          :is-scrolled="isScrolled"
          :show-details-header="showDetailsHeader"
          :can-edit-info="canEditInfo"
          :can-assign-responsible="$checkPermissions(['assign_responsible_user'])"
          :can-edit-client="isClientEditAllowed"
          :company-contract-prefixes="companyContractPrefixes"
          :zones="zones"
          :users-list="$store.state.users.usersList"
          :project-line-associations="projectLineAssociations"
          :user-info="userInfo"
          :user-responsible="userResponsible"
          :contract-value="contractValue"
          :contract-payment-required="contractPaymentRequired"
          :project-type-subtitle="projectTypeSubtitle"
          :project-type-title="projectTypeTitle"
          :required-rule="requiredRule"
          :can-edit-contract-value="true"
          @change-contract-prefix="changeContractPrefix"
          @edit-project-data="editProjectData"
          @go-to="handleGoTo"
          @change-ipt-zone="changeIptZone"
          @change-project-type="changeProjectType"
          @change-project-destiny="changeProjectDestiny"
          @change-responsible="changeResponsible"
          @edit-user="editUser"
          @change-payment-required="changePaymentRequired"
          @send-payment-request-email="sendPaymentRequestEmail"
        />

        <ContractToolbar
          v-if="showContractToolbar"
          :contract="contract"
          :isEditProhibited="isEditProhibited"
          :contractHeaderHeight="contractHeaderHeight"
          :isScrolled="isScrolled"
          :contractPreviewing="contractPreviewing"
          :loadingPdf="loadingPdf"
          :showPreviewButton="showPreviewButton"
          :canSignCompany="canSignCompany"
          :viewPdfText="viewPdfText"
          :contractAddendums="contractAddendums"
          :contractStatuses="contractStatuses"
          :otherContractAddendums="otherContractAddendums"
          :paymentValidation="showPaymentValidation ? paymentsCoverageValid : undefined"
          :fromModal="fromModal"
					:showingObservations="showingObservations"
          @preview-contract="previewContract"
          @company-sign="companyRepSign"
          @contract-to-pdf="contractToPdf"
          @open-contract-pdf="openContractPDF"
          @start-addendum="startContractAddendum"
          @template-selected="templateContentSelected"
          @reload-contract-info="getContractInfo"
          @toggle-observations="toggleObservations"
          @start-delete="startDeleteContract"
          @to-parent-contract="toParentContract"
        />

        <!-- Cargando preview -->
        <template v-if="showLoadingPreview">
          <p class="text-center caption">Cargando {{ documentLabel }}</p>
          <v-skeleton-loader type="heading"   class="my-2 mx-6" />
          <v-skeleton-loader type="paragraph" class="my-2 mx-6" />
          <v-skeleton-loader type="text"      class="my-2 mx-6" />
          <v-skeleton-loader type="paragraph" class="my-2 mx-6" />
        </template>

        <!-- Vista no editable -->
        <component
          v-else-if="showNonEditableView"
          :is="nonEditableComponent"
          :contract_info="contract"
          :fromCompany="true"
        />

        <!-- Editor de texto (preview) -->
        <component
          v-else-if="showTextEditorView"
          :is="editorComponent"
          :contract="contract"
        />

        <!-- Editor de secciones completo -->
        <div v-else-if="showSectionEditor" class="contract-container flex-grow-1 pb-0">

          <!-- Cabecera del documento -->
          <div class="py-2 px-4" style="flex-wrap: wrap;">
            <div class="d-flex justify-space-between align-stretch">
              <div
                class="d-flex flex-column justify-center align-items-center flex-grow-1"
                :style="`background-image: url(${selectedCompany.company.logo});`"
                style="background-size: contain; background-repeat: no-repeat;"
              />
              <div class="text-end flex-grow-2 mr-1">
                <p class="font-weight-bold mb-0" style="color: #606060; font-size: 19.6px !important;">
                  {{ $store.state.companies.selectedCompany.company.name }}
                </p>
                <p class="caption mb-0" style="color: #606060; line-height: 11pt;">{{ $store.state.companies.selectedCompany.company.description }}</p>
                <p class="caption mb-0" style="color: #606060; line-height: 11pt;">Rut: {{ $store.state.companies.selectedCompany.company.rut | formatRut }}</p>
                <p class="caption mb-0" style="color: #606060; line-height: 11pt;">Fono: {{ $store.state.companies.selectedCompany.company.phone }}</p>
                <p class="caption mb-3" style="color: #606060; line-height: 11pt;">Email: {{ $store.state.companies.selectedCompany.company.email }}</p>
                <p class="caption mb-0" style="color: #606060; line-height: 11pt;">Código: {{ documentCode }}</p>
                <p class="caption mb-0" style="color: #606060; line-height: 11pt;">Fecha: {{ contract.created_at | toDate }}</p>
                <p v-if="showStartDate" class="caption mb-0" style="color: #606060; line-height: 11pt;">
                  Inicio Proyecto:
                  <b v-if="contract.start_date">{{ contract.start_date | toDate }}</b>
                  <b v-else>A Convenir</b>
                </p>
              </div>
            </div>
          </div>

          <!-- Secciones arrastrables -->
          <draggable
            v-if="contract"
            v-model="contract.sections"
            group="project_contract"
            id="contracts"
            item-key="id"
            :move="onMove"
            filter=".exclude"
            :disabled="showingObservations"
            @end="startEndDragEventCategory"
          >
            <div
              v-for="(section, index) in contract.sections"
              :key="index"
              class="py-2 px-4"
              :class="{ 'exclude': isTitleSection(section) }"
            >
              <div v-if="isSectionVisible(section)" class="section-container rounded">
                <div class="pa-4">
                  <div class="d-flex align-center mb-3 justify-space-between" v-if="!showingObservations">

                    <!-- Edición del título de sección -->
                    <div class="pa-2 w-100" v-if="isEditingSection(section)">
                      <v-text-field
                        label="Título" placeholder="Título"
                        v-model="newSection.title"
                        class="custom-height-search custom-height-calendar mb-2"
                        hide-details dense outlined color="black"
                      />
                      <div class="d-flex justify-start align-center">
                        <v-btn depressed color="#FF120B" class="my-2 mx-2" small outlined @click="closeNewSection">
                          <v-icon>mdi-window-close</v-icon>
                          <p class="mb-0 ml-2 caption">Cancelar</p>
                        </v-btn>
                        <v-btn depressed color="green" dark class="my-2" small @click="confirmUpdateSection">
                          <v-icon>mdi-check</v-icon>
                          <p class="mb-0 ml-2 caption">Confirmar Cambios</p>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Visualización del título de sección -->
                    <div class="d-flex flex-grow-1 justify-space-between" v-else>
                      <div v-if="isTitleSection(section)" style="width: 100%;">
                        <div class="d-flex align-center justify-center flex-grow-1">
                          <div class="d-flex flex-column align-center justify-center" style="width: 100%;">
                            <p class="mb-0 font-weight-bold text-center flex-grow-1">{{ section.title }}</p>
                            <p class="mb-0 font-weight-bold text-center flex-grow-1" v-if="section.subtitle">{{ section.subtitle }}</p>
                          </div>
                        </div>
                      </div>
                      <p class="mb-0 font-weight-bold" v-else>{{ sectionIndex(index) }} {{ section.title }}</p>

                      <div class="d-flex align-center">
                        <ContractTitlesMenu
                          v-if="isTitleSection(section)"
                          :contract="contract"
                          :section="section"
                          :getContractInfo="() => getContractInfo()"
                          :closeNewSection="() => closeNewSection()"
                        />
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn depressed color="warning" small icon v-bind="attrs" v-on="on" @click="editNewSection(section)">
                                <v-icon small>mdi-pencil-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Editar Sección</span>
                          </v-tooltip>
                        </template>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn depressed color="#FF120B" small icon v-bind="attrs" v-on="on" @click="deleteSection(section)">
                              <v-icon small>mdi-delete-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>Eliminar Sección</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              depressed color="green" small icon
                              v-bind="attrs" v-on="on"
                              v-show="addingContent !== section.id"
                              @click="addNewContent(section)"
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          <span>Agregar Contenido a Sección</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>

                  <!-- Contenidos arrastrables -->
                  <draggable
                    v-model="section.contents"
                    group="project_contract_sections"
                    :id="'contracts_sections_' + index"
                    item-key="id"
                    :disabled="showingObservations"
                    @end="(ev) => startEndDragEventSection(ev)"
                  >
                    <div
                      v-for="(section_content, indexB) in section.contents"
                      :key="'a' + indexB"
                      :id="'section_content_id_' + section_content.id"
                      class="mb-4"
                    >
                      <v-hover v-slot="{ hover }">
                        <div class="section-content-container">

                          <!-- Edición de contenido -->
                          <div class="pa-2" v-if="isEditingContent(section_content)">
                            <v-text-field
                              v-model="newContent.title"
                              label="Título" placeholder="Ingrese título identificador"
                              outlined required dense hide-details :rules="requiredRule"
                            />
                            <v-checkbox v-model="newContent.show_title" class="mb-2" color="black" label="Mostrar Título en Contrato" hide-details dense />
                            <ContractTinyMCE
                              :textContent="newContent.content"
                              :simpleEditor="true"
                              @update:textContent="updateContent"
                            />
                            <div class="d-flex justify-end align-center">
                              <v-btn depressed color="#FF120B" class="my-2 mx-2" small outlined @click="closeNewContent">
                                <v-icon>mdi-window-close</v-icon>
                                <p class="mb-0 ml-2 caption">Cancelar</p>
                              </v-btn>
                              <v-btn depressed color="green" dark class="my-2" small @click="confirmUpdateContent">
                                <v-icon>mdi-check</v-icon>
                                <p class="mb-0 ml-2 caption">Confirmar Cambios</p>
                              </v-btn>
                            </div>
                          </div>

                          <!-- Visualización de contenido -->
                          <div class="px-2" v-else>
                            <div v-if="section_content.show_title" class="font-weight-bold">
                              {{ (indexB + 1) + '.- ' }} {{ section_content.title }}
                            </div>
                            <div class="section-content pa-2" v-html="transformPlaceholdersToContentView(section_content.content)" />

                            <div class="d-flex justify-end py-2">
                              <v-tooltip left>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-badge
                                    :value="countContractsByContentId(section_content.id)"
                                    :content="countContractsByContentId(section_content.id)"
                                    color="red" overlap
                                  >
                                    <v-btn
                                      v-show="section_content.observation"
                                      :ref="'commentActivatorButton_' + section_content.id"
                                      depressed color="warning" outlined small rounded
                                      @click="openCommentSection(section_content.id)"
                                      v-bind="attrs" v-on="on"
                                    >
                                      <v-icon class="mr-4" :class="{ 'pulse-animation': section_content.observation && !hover }">
                                        mdi-comment-plus-outline
                                      </v-icon>
                                        <p class="mb-0">
                                          {{ (contract && contract.last_status && contract.last_status.status >= contractStatuses.ACEPTADO)
                                            ? 'Ver Observaciones / Comentarios Previos'
                                            : 'Hay Observaciones / Comentarios en esta sección' }}
                                        </p>
                                    </v-btn>
                                  </v-badge>
                                </template>
                                <span>
																	{{ (contract && contract.last_status && contract.last_status.status >= contractStatuses.ACEPTADO)
																		? 'Ver Observaciones / Comentarios Previos en Sección'
																		: 'Ver / Agregar Comentarios en Sección' }}
																</span>
                              </v-tooltip>
                            </div>
                          </div>

                          <!-- Hover: acciones sobre el contenido -->
                          <div class="hover-section-content px-0 py-2" v-show="hover && !isEditingContent(section_content) && !showingObservations">
                            <div class="d-flex flex-column w-100 h-100 fill-height">
                              <div class="d-flex justify-center align-center flex-grow-1 px-2 py-1" v-if="isTitleSection(section)">
                                <p class="mb-0 text-center">Esta sección no se puede modificar</p>
                              </div>
                              <div class="d-flex flex-column justify-center align-center flex-grow-1" v-else>
                                <!-- Copiar a sección -->
                                <v-menu ref="menu" v-model="menuCopyContentOpen[section_content.id]" offset-y auto eager origin="center center">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn color="green" icon small v-bind="attrs" v-on="on">
                                      <v-icon small>mdi-content-copy</v-icon>
                                    </v-btn>
                                  </template>
                                  <div class="white d-flex flex-column" style="max-width: 400px;">
                                    <v-list class="template-list white py-0" max-height="300px" dense>
                                      <v-list-item dense class="mb-1 d-flex" style="min-height: 25px; background: #959595;">
                                        <p class="caption flex-grow-1 mb-0 font-weight-bold white--text">Copiar Contenido a Nueva Sección:</p>
                                        <v-btn color="white" icon @click="menuCopyContentOpen[section_content.id] = false"><v-icon>mdi-close</v-icon></v-btn>
                                      </v-list-item>
                                      <v-list-item
                                        v-for="(sec, indexSelect) in contract.sections"
                                        :key="'tselect-' + index + '_' + indexSelect"
                                        class="template-list-item white"
                                        @click="copyContentToSection(section_content, indexSelect)"
                                      >
                                        <v-list-item-content>
                                          <v-list-item-title>{{ indexSelect > 0 ? sectionIndex(indexSelect) : '' }} {{ sec.title }}</v-list-item-title>
                                        </v-list-item-content>
                                      </v-list-item>
                                    </v-list>
                                  </div>
                                </v-menu>

                                <!-- Mover a sección -->
                                <v-menu ref="menu" v-model="menuMoveContentOpen[section_content.id]" offset-y auto eager origin="center center">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn color="#524D4D" icon small v-bind="attrs" v-on="on">
                                      <v-icon small>mdi-swap-horizontal</v-icon>
                                    </v-btn>
                                  </template>
                                  <div class="white d-flex flex-column" style="max-width: 400px;">
                                    <v-list class="template-list white py-0" max-height="300px" dense>
                                      <v-list-item dense class="mb-1 d-flex" style="min-height: 25px; background: #959595;">
                                        <p class="caption flex-grow-1 mb-0 font-weight-bold white--text">Mover Contenido a Nueva Sección:</p>
                                        <v-btn color="white" icon @click="menuMoveContentOpen[section_content.id] = false"><v-icon>mdi-close</v-icon></v-btn>
                                      </v-list-item>
                                      <v-list-item
                                        v-for="(sec, indexSelect) in contract.sections"
                                        :key="'tselect-' + index + '_' + indexSelect"
                                        class="template-list-item white"
                                        @click="moveContentToSection(section_content, index, indexSelect)"
                                      >
                                        <v-list-item-content>
                                          <v-list-item-title>{{ indexSelect > 0 ? sectionIndex(indexSelect) : '' }} {{ sec.title }}</v-list-item-title>
                                        </v-list-item-content>
                                      </v-list-item>
                                    </v-list>
                                  </div>
                                </v-menu>

                                <v-btn depressed color="warning" icon small @click="editNewContent(section_content)">
                                  <v-icon small>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn depressed color="#FF120B" icon small @click="deleteContent(section_content)">
                                  <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>

                          <ObservationOverlay
                            v-if="menuComment == section_content.id"
                            :contract="contract"
                            :commentPosition="commentPosition"
                            :section_content="section_content"
                            :side="2"
                            @closeObservationOverlay="closeCommentForm"
                            @reloadInformation="getContractInfo"
                          />
                        </div>
                      </v-hover>
                    </div>
                  </draggable>
                </div>

                <!-- Agregar contenido a sección -->
                <div class="pa-4" v-if="addingContent == section.id">
                  <v-divider class="mb-4" />
                  <div class="mb-3 d-flex justify-space-between align-center">
                    <div>
                      <p class="mb-0 caption font-weight-bold">Agregar Contenido a Sección</p>
                      <p class="mb-0 caption text--secondary">Puedes seleccionar una sección y un contenido preestablecido</p>
                    </div>
                    <div style="width: 30%">
                      <v-select
                        v-if="!blockCategorySelect"
                        v-model="selectedCategory"
                        :items="companyContractCategories"
                        :loading="$store.state.contracts.loadingCompanyContractCategories"
                        :menu-props="{ top: true, offsetY: true }"
                        item-text="title" item-value="id"
                        label="Seleccione Sección"
                        class="custom-height-search custom-height-calendar mb-2 w-100"
                        outlined dense return-object required
                      />
                      <v-select
                        v-model="selectedContent"
                        :items="$store.state.contracts.companyContracts"
                        :loading="$store.state.contracts.loadingCompanyContracts"
                        :menu-props="{ top: true, offsetY: true }"
                        item-text="title" item-value="id"
                        label="Seleccione Contenido"
                        class="custom-height-search custom-height-calendar w-100"
                        outlined dense return-object required clearable
                      />
                    </div>
                  </div>
                  <v-text-field
                    v-model="newContent.title"
                    label="Título" placeholder="Ingrese título identificador"
                    outlined required dense hide-details :rules="requiredRule"
                  />
                  <v-checkbox v-model="newContent.show_title" class="mb-2" color="black" label="Mostrar Título en Contrato" hide-details dense />
                  <ContractTinyMCE :textContent="newContent.content" :simpleEditor="true" @update:textContent="updateContent" />
                  <div class="d-flex">
                    <v-btn depressed color="#FF120B" class="my-2 mx-2" small outlined @click="closeNewContent">
                      <v-icon>mdi-close-circle-outline</v-icon>
                      <p class="mb-0 ml-2 caption">Cancelar</p>
                    </v-btn>
                    <v-btn depressed color="green" class="my-2 mx-2 flex-grow-1" small dark @click="confirmContent">
                      <v-icon>mdi-check-circle-outline</v-icon>
                      <p class="mb-0 ml-2 caption">Confirmar</p>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </draggable>

          <!-- Agregar sección -->
          <div class="py-2 px-4" v-if="!showingObservations">
            <div class="section-container rounded">
              <div class="pa-4">
                <v-btn
                  v-if="!addingSection"
                  depressed color="#959595" class="my-2 flex-grow-1" small block outlined
                  @click="addNewSection"
                >
                  <v-icon>mdi-plus</v-icon>
                  <p class="mb-0 ml-2 caption">Agregar Sección</p>
                </v-btn>

                <div v-else>
                  <div class="mb-3 d-flex justify-space-between align-center">
                    <div>
                      <p class="mb-0 caption font-weight-bold">Agregar una Sección</p>
                      <p class="mb-0 caption text--secondary">Puedes seleccionar una sección preestablecida</p>
                    </div>
                    <div style="width: 30%">
                      <v-select
                        v-model="selectedCategory"
                        :items="companyContractCategories"
                        :loading="$store.state.contracts.loadingCompanyContractCategories"
                        :menu-props="{ top: true, offsetY: true }"
                        item-text="title" item-value="id"
                        label="Seleccione Sección"
                        class="custom-height-search custom-height-calendar mb-2 w-100"
                        outlined dense return-object required clearable
                        @change="onChangeCategory"
                      />
                    </div>
                  </div>
                  <v-text-field
                    v-model="newSection.title"
                    label="Título" placeholder="Ingrese título de la sección"
                    outlined required dense :rules="requiredRule"
                  />
                  <div class="d-flex">
                    <v-btn depressed color="green" class="my-2 mx-2 flex-grow-1" small dark @click="confirmSection">
                      <v-icon>mdi-check-circle-outline</v-icon>
                      <p class="mb-0 ml-2 caption">Confirmar</p>
                    </v-btn>
                    <v-btn depressed color="#FF120B" class="my-2 mx-2" small outlined @click="closeNewSection">
                      <v-icon>mdi-close-circle-outline</v-icon>
                      <p class="mb-0 ml-2 caption">Cancelar</p>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- ── Columna lateral ── -->
      <v-col
        :cols="sidebarColSize"
        v-if="showSidebar"
        class="d-flex flex-column fill-height pb-0"
        style="overflow: scroll;"
      >
        <div class="mb-4">
          <div class="d-flex align-center justify-space-between">
            <p class="mb-0 caption font-weight-bold">Secciones de {{ mode == 'subcontract' ? 'Subcontrato' : 'Contrato' }}</p>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn depressed color="#959595" icon class="mb-0" small v-bind="attrs" v-on="on" @click="editCompanySection">
                  <v-icon small>mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>Editar Secciones de {{ mode == 'subcontract' ? 'Subcontrato' : 'Contrato' }}</span>
            </v-tooltip>
          </div>
          <p class="mb-0 caption">Arrastra cualquiera de estas secciones al {{ mode == 'subcontract' ? 'subcontrato' : 'contrato' }} para agregarla</p>
          <draggable
            v-model="companyContractCategories"
            :group="{ name: 'project_contract', pull: 'clone', put: false }"
            id="contracts" item-key="id"
            @end="startEndDragNewCategory"
          >
            <div
              v-for="item in companyContractCategories"
              :key="'cats-' + item.id"
              class="custom-list-drag pa-2 rounded my-2"
              :class="{ 'selected': item.id == selectedCategory?.id }"
              @click="onClickCategory(item)"
            >
              <p class="mb-0 caption">{{ item.title }}</p>
            </div>
          </draggable>
        </div>

        <div>
          <p class="mb-0 caption font-weight-bold">Contenido Sección de {{ mode == 'subcontract' ? 'Subcontrato' : 'Contrato' }} Seleccionada</p>
          <p class="mb-0 caption">Arrastra cualquiera de estos contenidos a una sección ya presente en el {{ mode == 'subcontract' ? 'subcontrato' : 'contrato' }} para agregarlo</p>
          <template v-if="!isSelectedCategoryType3">
            <draggable
              v-model="companyContracts"
              :group="{ name: 'project_contract_sections', pull: 'clone', put: false }"
              id="contracts" item-key="id"
              class="draggable-contents"
              @end="startEndDragNewContent"
            >
              <div v-for="item in companyContracts" :key="'compc-' + item.id">
                <div class="custom-list-drag pa-2 rounded my-2" v-if="isCompanyFirstSection(item)">
                  <p class="mb-0 font-weight-bold caption">{{ item.title }}</p>
                  <div class="caption pt-1" v-html="transformPlaceholdersToFriendlyView(item.content)" />
                </div>
              </div>
            </draggable>
          </template>
          <p v-else class="mb-0 caption">
            Selecciona una sección del listado superior para ver los contenidos que la empresa ha configurado
          </p>
        </div>
      </v-col>
    </v-row>

    <!-- Cargando contrato completo -->
    <div v-else>
      <p class="text-center caption">Cargando {{ documentLabel }}</p>
      <v-skeleton-loader type="heading"   class="my-2 mx-6" />
      <v-skeleton-loader type="paragraph" class="my-2 mx-6" />
      <v-skeleton-loader type="text"      class="my-2 mx-6" />
      <v-skeleton-loader type="paragraph" class="my-2 mx-6" />
    </div>

    <CostBreakdownSummaryTablesModal
      v-if="(mode === 'contract' || mode === 'addendum') && $store.state.costbreakdown.costBreakdownSummaryTablesModal"
      :costs-table="costsTable"
      :contract="contract"
      :project="project"
      :saveSelfPaymetsConfig="true"
    />

    <CostBreakdownSubcontractTablesModal
      v-if="isSubcontract && $store.state.costbreakdown.costBreakdownSubcontractTablesModal"
      :costs-table="costsTable"
      :contract="contract"
      :project="project"
      :saveSelfPaymetsConfig="true"
    />

    <SimpleNotificationListener
      v-if="simpleNotificationPath"
      :notificationPath="simpleNotificationPath"
      @onNotification="_onReloadInformation"
    />
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import _debounce from 'lodash/debounce'
import {
  getContract, updateContract,
  createContractSectionProject, createContractSectionContent,
  updateContractSectionContent, updateContractSectionProject,
  deleteContractSectionProject, deleteContractSectionContent,
  updateContractSectionProjectOrders, updateContractSectionContentOrders,
  setTemplateToContract, createContractTemplate, deleteContractTemplate,
  createContractTitle, updateContractTitle, deleteContractTitle,
  createContractSubtitle, updateContractSubtitle, deleteContractSubtitle,
  createContractPDF, sendContractPdfEmail, createContractPdfFromPreview,
  sendContractPaymentRequest, addContractAddendum,
} from '@/helpers/api/contracts'
import { markAsReaded } from '@/helpers/api/notifications'
import { updateProjectInfo } from '@/helpers/api/projects'
import { eventBus } from '@/components/Helpers/Events/NotificationsEventBus'
import { validatePaymentsFullyCovered } from '@/helpers/tablecostHelpers'
import { setResponsible as setResponsiblePreProject } from '@/helpers/api/preprojects'
import { setResponsible as setResponsibleProject } from '@/helpers/api/projects'
import { editCrmResponsible } from '@/helpers/api/crm'
import {
  placeholderMapping, getContractContext,
  transformPlaceholdersToRealContent, reverseContentToPlaceholders,
} from '@/components/Helpers/Constants/contract'
import contractStatuses from '@/components/Helpers/Constants/contract.js'
import { getProjectCode } from '@/helpers/helpers'

import ProjectHeaderInfo from '@/components/Views/Projects/Parts/ProjectHeaderInfo'
import ContractTinyMCE from '@/components/Helpers/Components/ContractTinyMCE'
import ContractTitlesMenu from '@/components/Helpers/Components/ContractTitlesMenu'
import ObservationOverlay from '@/components/Views/Contracts/Parts/ObservationOverlay'
import ContractToolbar from '@/components/Views/Contracts/Parts/ContractToolbar'
import SimpleNotificationListener from '@/components/Helpers/Notifications/SimpleNotificationListener'
import CostBreakdownSummaryTablesModal from '@/components/Modals/CostBreakdown/CostBreakdownSummaryTablesModal'
import CostBreakdownSubcontractTablesModal from '@/components/Modals/CostBreakdown/CostBreakdownSubcontractTablesModal'

// ─── Mapas de tipos morphable ────────────────────────────────────────────────

const MORPHABLE_TYPE_MAP = {
  'App\\Models\\BotConversation': 'crm',
  'App\\Models\\PreProject':      'preproject',
  'App\\Models\\Project':         'project',
}

const MORPHABLE_LABEL_MAP = {
  'App\\Models\\BotConversation': 'CRM / Visita Técnica',
  'App\\Models\\PreProject':      'PreProyecto',
  'App\\Models\\Project':         'Proyecto',
}

const defaultNewSection = () => ({
  contract_id:  null,
  section_type: 2,
  title:        null,
  subtitle:     null,
  order:        1,
})

const defaultNewContent = () => ({
  title:               null,
  order:               1,
  content:             null,
  contract_section_id: null,
  show_title:          false,
})

// ─── Componente ─────────────────────────────────────────────────────────────

export default {
  name: 'ContractEditorCore',

  components: {
    draggable, ProjectHeaderInfo, ContractTinyMCE, ContractTitlesMenu,
    ObservationOverlay, ContractToolbar, SimpleNotificationListener,
    CostBreakdownSummaryTablesModal, CostBreakdownSubcontractTablesModal,
  },

  props: {
    // ID del documento (puede venir como prop desde modal, o se lee de $route.query)
    contractId: { type: [Number, String], default: null },

    // Identificador del tipo: 'contract' | 'addendum' | 'subcontract'
    mode: { type: String, default: 'contract' },

    // Componente a usar para la vista no editable
    nonEditableComponent: { type: [Object, String], required: true },

    // Componente a usar para el editor de texto/preview
    editorComponent: { type: [Object, String], required: true },

    // Opciones de comportamiento por tipo
    fromModal:          { type: Boolean, default: false },
    showStartDate:      { type: Boolean, default: true },
    showPaymentValidation: { type: Boolean, default: false },
    isSubcontract:      { type: Boolean, default: false },

    // Función para obtener el ID del contrato padre (para toParentContract)
    parentContractIdGetter: { type: Function, default: null },

    // Función extra a ejecutar después de cargar el contrato (hook post-fetch)
    onContractLoaded: { type: Function, default: null },

    // Params extra para el fetch del contrato (ej: { get_addendums: true })
    fetchParams: { type: Object, default: () => ({}) },

    // Texto del label para skeletons y mensajes ("Contrato", "Anexo", "Subcontrato")
    documentLabel: { type: String, default: 'Contrato' },

    // Estado de la mutación de Vuex para cerrar el modal (si aplica)
    closeModalMutation: { type: String, default: null },

    // Estado en Vuex para from_state al asignar responsable
    responsableFromState: { type: String, default: 'contract' },
  },

  data() {
    return {
      // ── Contrato ──
      contract:             null,
      contractValue:        null,
      contractPaymentRequired: false,
      otherContractAddendums: null,
      placeholderMapping,
      contractStatuses,

      // ── UI ──
      loading:             false,
      loadingPdf:          false,
      showDetailsHeader:   true,

      // ── Edición de secciones ──
      addingSection:       false,
      addingContent:       false,
      newSection:          defaultNewSection(),
      newContent:          defaultNewContent(),
      selectedCategory:    null,
      selectedContent:     null,
      blockCategorySelect: false,
      selectedSectionToWork: null,

      // ── Menus / overlays ──
      menuCopyContentOpen: {},
      menuMoveContentOpen: {},
      menuComment:         null,
      commentPosition:     { top: 0, left: 0, width: 400 },

      // ── Estado de vista ──
      contractPreviewing:        false,
      loadingContractPreviewing: false,
      showNonEditable:           false,
      showingObservations:       false,
      deletedAddendumShow:       false,

      // ── Misc ──
      resizeObserver:  null,
      requiredRule:    [v => !!v || 'Campo requerido'],

      // Campos de compatibilidad (usados en métodos heredados)
      edditingTitle:    { id: null, title: null },
      edditingSubtitle: { id: null, subtitle: null },
      prefixMenu:       false,
      zoneMenu:         false,
      menuTitles:       false,
      menuSubtitles:    false,
      menuAddTitle:     false,
      menuAddSubtitle:  false,
      menuTemplates:    false,
      menuTemplate:     false,
      contractValueMenu: false,
      menuStatuses:     false,
      templateName:     null,
      contractTitle:    null,
      contractSubtitle: null,
      paymentValidation: null,
      userCompanyMenu:  false,
      showAddNewTemplate: false,
      responsibleMenu:  false,
      search:           '',
    }
  },

  // ─── Computed ─────────────────────────────────────────────────────────────

  computed: {
    // ── Layout ──
    isNarrowViewport() { return this.$vuetify.breakpoint.width < 1300 },
    isPreviewing()     { return this.contract && this.selectedCompany && this.contractPreviewing },
    containerHeightStyle() {
      return `height:${this.$store.getters['general/availableHeight']}px; overflow: scroll;`
    },
    mainColSize() {
      if (this.isPreviewing || this.loadingContractPreviewing) return '12'
      if (!this.showSidebar) return '12'
      return '9'
    },
    sidebarColSize() { 
      return '3' 
    },

    // ── Visibilidad de bloques ──
    showContractToolbar()  { return !!(this.contract && this.canShowContractDetail) },
    showLoadingPreview()   { return !!(this.contract && this.selectedCompany && this.loadingContractPreviewing && this.canShowContractDetail) },
    showNonEditableView() {
      if (!this.canShowContractDetail || this.showingObservations) return false
      return (
        this.isEditProhibited ||
        this.contract?.last_status?.status >= contractStatuses.ACEPTADO ||
        (this.contract && this.selectedCompany && this.showNonEditable)
      )
    },
    showTextEditorView() {
      return !!(this.contract && this.selectedCompany && this.contractPreviewing && this.canShowContractDetail && !this.showingObservations)
    },
    showSectionEditor()  { return !!(this.contract && this.selectedCompany && this.canShowContractDetail) },
    showSidebar() {
      if (!this.canShowContractDetail || this.isPreviewing) return false
      const s = this.contract?.last_status?.status
      if (s !== undefined && s >= contractStatuses.ENVIADO_CONTRAPARTE) return false
      return true
    },
    isSelectedCategoryType3() { return this.selectedCategory?.section_type === 3 },

    // ── Permisos ──
    canShowContractDetail() { return !!(this.contract && this.selectedCompany) },
    canEditInfo()           { return !this.contract?.morphable?.pl_signed_contract },
    isClientEditAllowed()   { return this.contract?.morphable?.responsible_id === this.userInfo.id && this.canEditInfo },
    isEditProhibited()      { return this.contract?.morphable?.responsible_id !== this.userInfo?.id },
    canSignCompany() {
      if (!this.contract) return false
      if (this.userInfo && this.selectedCompany && this.selectedCompany.company_id === this.contract.company_id) {
        return this.contract.last_status?.status === contractStatuses.FIRMADO_CONTRAPARTE &&
          this.selectedCompany.company?.responsible === this.userInfo.id
      }
      return false
    },
    showPreviewButton() {
      if (!this.contract) return false
      const s = this.contract.last_status?.status
      return s < contractStatuses.ENVIADO_CONTRAPARTE || s === contractStatuses.OBSERVADO_CONTRAPARTE
    },

    // ── Datos del contrato ──
    project()      { return this.contract?.morphable },
    costsTable()   { return this.contract?.cost_table },
    documentCode() {
      if (!this.contract || !this.project) return ''
      return getProjectCode(this.contract, this.project)
    },
    simpleNotificationPath() {
      if (!this.project) return null
      return `notifications/project_cost_update/${this.project?.id}`
    },
    userResponsible() {
      const p = this.contract?.morphable?.responsible?.profile
      if (!p) return null
      return [p.name, p.lastname, p.second_lastname].filter(Boolean).join(' ')
    },
    contractAddendums() {
      return this.deletedAddendumShow
        ? (this.contract?.deleted_addendums || [])
        : (this.contract?.addendums || [])
    },
    projectLineAssociations() { return this.contract?.morphable?.associations },
    projectTypeTitle() {
      return this.contract?.info?.project_type?.name || this.project?.info?.project_type?.name || 'Tipo de Proyecto No Definido'
    },
    projectTypeSubtitle() {
      const p = this.contract?.info?.project_type?.parent?.name   || this.project?.info?.project_type?.parent?.name   || ''
      const c = this.contract?.info?.project_type?.category?.name || this.project?.info?.project_type?.category?.name || ''
      if (p && c) return `${c} | ${p}`
      return p || c || ''
    },
    viewPdfText() {
      const s = this.contract?.last_status?.status
      if (s === contractStatuses.FIRMADO_CONTRAPARTE)  return 'Ver PDF Firmado Por Cliente'
      if (s === contractStatuses.FIRMADO_AMBAS_PARTES) return 'Ver PDF Firmado Por Ambas Partes'
      return 'Ver PDF del Contrato'
    },
    paymentsCoverageValid() {
      if (!this.costsTable) return null
      return validatePaymentsFullyCovered(this.costsTable)
    },
    contractVariablesValue() { return this.contract ? getContractContext(this.contract) : null },

    // ── ID efectivo del documento ──
    effectiveContractId() {
      return this.contractId ?? this.$route?.query?.id ?? null
    },

    // ── Store shortcuts ──
    selectedCompany()           { return this.$store.state.companies.selectedCompany },
    userInfo()                  { return this.$store.state.user.userInfo },
		isClient()                  { return this.userInfo?.id === this.contract?.morphable?.client?.id },
    zones()                     { return this.$store.state.projects.zoneIpt },
    companyContractCategories() { return this.$store.state.contracts.companyContractCategories },
    companyContracts()          { return this.$store.state.contracts.companyContracts },
    contractHeaderHeight()      { return this.$store.state.contracts.contractHeaderHeight },
    isScrolled()                { return this.$store.state.contracts.scrolledContract > 0 },
    companyContractPrefixes() {
      return this.$store.state.budget.companiesBudgetPrefixes.filter(p => p.type === 'contract')
    },
    queryRoute() { return this.$route?.query },
  },

  // ─── Watchers ─────────────────────────────────────────────────────────────

  watch: {
    selectedCategory(newValue) {
      if (newValue) {
        this.$store.dispatch('contracts/getCompanyContractsConfig', { category_id: newValue.id, start: 0, limit: 100 })
      }
    },
    selectedContent(newValue) {
      this.newContent.title                       = newValue?.title   ?? null
      this.newContent.content                     = newValue?.content ?? ''
      this.newContent.company_contract_section_id = newValue?.id     ?? null
    },
    selectedCompany(newValue) {
      if (newValue) this.loadCompanyData()
    },
    menuTemplate() {
      this.$nextTick(() => { this.showAddNewTemplate = false })
    },
    queryRoute() {
      if (!this.$route) return
      const contractId = this.$route.query.id
      if (this.contract && this.contract.id != contractId) {
        this.contract = null
        this.getContractInfo(true)
      }
    },
    effectiveContractId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.contract = null
        this.getContractInfo(true)
      }
    },
    contract(val) {
      if (!val) return
      this.$nextTick(() => {
        this.initResizeObserver()
        if (this.isEditProhibited) {
          this.setMessage({ message: 'Sólo el responsable puede editar el contrato. Puedes observar la información.', title: 'Aviso', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
        }
        if (this.contract?.morphable?.pl_signed_contract) {
          this.$store.dispatch('general/addMessage', {
            message: 'Este Contrato está en una línea de proyecto con un contrato firmado, por lo tanto la información del proyecto no puede ser editada.',
            title: 'Línea de Proyecto con Contrato', created: new Date(), type: 'warning', icon: 'mdi-exclamation', color: 'orange',
          })
        }
      })
    },
    '$route.hash'() { this.scrollToSection() },
  },

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  mounted() {
    this.scrollToSection()
    this.initResizeObserver()
    if (this.selectedCompany) this.loadCompanyData()
    this.getContractInfo(true)
    eventBus.$on('reloadInformation', this._onReloadInformation)
    this.setupPaymentsButtonListener()
  },

  beforeDestroy() {
    eventBus.$off('reloadInformation', this._onReloadInformation)
  },

  // ─── Methods ──────────────────────────────────────────────────────────────

  methods: {
    // ── Helpers de template ──────────────────────────────────────────────

    isTitleSection(section)        { return section.section_type === 1 },
    isSectionVisible(section)      { return !this.selectedSectionToWork || this.selectedSectionToWork === section.id },
    isEditingSection(section)      { return this.newSection.contract_section_id === section.id },
    isEditingContent(content)      { return this.newContent.contract_section_content_id === content.id },

    // ── Carga de datos ───────────────────────────────────────────────────

    loadCompanyData() {
			const type = this.mode == 'subcontract' ? 2 : 1
      this.$store.dispatch('contracts/getCompanyContractCategoriesConfig', { type, start: 0, limit: 100 })
      this.$store.dispatch('contracts/getContractTemplates', { start: 0, limit: 100 })
      this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 })
      this.$store.dispatch('projects/getAllZoneIpts')
      this.$store.dispatch('users/getUserFromCompany')
    },

    async getContractInfo(showLoader = false) {
      if (!this.effectiveContractId) return
      if (showLoader) this.loading = true

      const contract = await getContract({ contract_id: this.effectiveContractId, ...this.fetchParams })
      if (contract.code === 200) {
        // Hook post-fetch (ej: addendum guarda otherContractAddendums)
				this.$emit('handle-contract-addendums', contract)
        if (this.onContractLoaded) this.onContractLoaded(contract)

        setTimeout(() => { this.scrollToSection() }, 500)
        this.$nextTick(() => {
          this.contract = contract.contract
          if (this.contract.contract_info) {
            this.contractValue           = this.contract.contract_info.contract_value
            this.contractPaymentRequired = this.contract.contract_info.payment_required
          }
        })
      }
      this.loading = false
    },

    // ── Vista ────────────────────────────────────────────────────────────

    previewContract() {
      if (this.contractPreviewing) { this.contractPreviewing = false; return }
      this.loadingContractPreviewing = true
      setTimeout(() => { this.contractPreviewing = true; this.loadingContractPreviewing = false }, 100)
    },

    toggleObservations()  { this.showingObservations = !this.showingObservations },
    openContractPDF()   {
			// this.$store.commit('contracts/setContractPdfModal', this.contract)
			if(!this.contract?.pdf?.file){
				this.setMessage({ message: 'El PDF del contrato no está disponible', title: 'Error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
				return
			}

			const screenWidth = window.screen.width;
			const screenHeight = window.screen.height;
			const finalWidth = Math.min(1000, screenWidth);

			window.open(
				`${this.contract?.pdf?.file?.file_link}`,
				'',
				`width=${finalWidth},height=${screenHeight},left=${finalWidth},top=0,toolbar=no,menubar=no,location=no,status=no`
			);
		},

    // ── Acción "ir al contrato padre" (addendum y subcontract) ───────────

    toParentContract() {
      if (!this.parentContractIdGetter) return
      const parentId = this.parentContractIdGetter(this.contract)
      this.$store.commit('contracts/setContractComponentModalId', parentId)
    },

    // ── Secciones ────────────────────────────────────────────────────────

    addNewSection() {
      this.selectedCategory = null
      this.selectedContent  = null
      this.newSection = { ...defaultNewSection(), contract_id: this.contract.id, order: this.contract.sections.length + 1 }
      this.addingSection = true
    },
    editNewSection(section) {
      this.newSection = { title: section.title, subtitle: section.subtitle, contract_section_id: section.id }
    },
    closeNewSection() { this.newSection = defaultNewSection(); this.addingSection = false },
    async confirmSection() { await createContractSectionProject(this.newSection); this.getContractInfo(); this.closeNewSection() },
    async confirmUpdateSection() { await updateContractSectionProject(this.newSection); this.getContractInfo(); this.closeNewSection() },
    async deleteSection(section) { await deleteContractSectionProject({ contract_section_id: section.id }); this.getContractInfo() },
    onChangeCategory(category) {
      this.newSection.title                   = category?.title        ?? null
      this.newSection.section_type            = category?.section_type ?? 2
      this.newSection.company_planning_cat_id = category?.id          ?? null
    },
    sectionIndex(index) {
      return `${this.$store.state.general.romanNumerals[index]}. ${this.$store.state.general.ordinalsNumbers[index]}:`
    },

    // ── Contenidos ───────────────────────────────────────────────────────

    addNewContent(section) {
      this.selectedContent     = null
      this.blockCategorySelect = !!section.company_planning_cat_id
      this.selectedCategory    = section.company_planning_cat_id
        ? { id: section.company_planning_cat_id, title: section.title }
        : null
      this.newContent    = { ...defaultNewContent(), contract_section_id: section.id, order: section.contents.length + 1 }
      this.addingContent = section.id
    },
    editNewContent(content) {
      this.newContent = { title: content.title, content: content.content, contract_section_content_id: content.id, show_title: content.show_title }
    },
    closeNewContent() { this.newContent = defaultNewContent(); this.addingContent = false },
    async confirmContent() { await createContractSectionContent(this.newContent); this.getContractInfo(); this.closeNewContent() },
    async confirmUpdateContent() { await updateContractSectionContent(this.newContent); this.getContractInfo(); this.closeNewContent() },
    async deleteContent(section_content) { await deleteContractSectionContent({ contract_section_content_id: section_content.id }); this.getContractInfo() },
    updateContent(content) { this.newContent.content = content },

    async copyContentToSection(section_content, to_index) {
      const new_section = this.contract.sections[to_index]
      const result = await createContractSectionContent({
        title: section_content.title, contract_section_id: new_section.id,
        order: (new_section.contents?.length ?? 0) + 1, content: section_content.content, show_title: section_content.show_title,
      })
      if (result.code === 200) this.getContractInfo()
    },

    async moveContentToSection(section_content, from_index, to_index) {
      const new_section = this.contract.sections[to_index]
      const old_section = this.contract.sections[from_index]
      const new_contents = [
        ...new_section.contents.map((s, i) => ({ id: s.id, order: i + 1, contract_section_id: new_section.id })),
        { id: section_content.id, order: new_section.contents.length + 1, contract_section_id: new_section.id },
      ]
      const old_contents = old_section.contents
        .filter(s => s.id !== section_content.id)
        .map((s, i) => ({ id: s.id, order: i + 1, contract_section_id: old_section.id }))
      const result = await updateContractSectionContentOrders({ contents: [...new_contents, ...old_contents] })
      if (result.code === 200) this.getContractInfo()
    },

    // ── Drag & drop ──────────────────────────────────────────────────────

    onMove(event) {
      return !(event.draggedContext.element.section_type === 1 || event.relatedContext.element.section_type === 1)
    },
    startEndDragEventCategory: _debounce(function (ev) { this.endDragEventCategory(ev) }, 800),
    async endDragEventCategory(ev) {
      if (typeof ev.newIndex !== 'number') return
      const sections = this.contract.sections.map((s, i) => ({ id: s.id, order: i + 1 }))
      const result = await updateContractSectionProjectOrders({ sections })
      if (result.code === 200) this.getContractInfo()
    },
    startEndDragEventSection: _debounce(function (ev) { this.endDragEventSection(ev) }, 800),
    async endDragEventSection(ev) {
      if (typeof ev.newIndex !== 'number') return
      const new_section = this.contract.sections[ev.to.id.replace('contracts_sections_', '')]
      const old_section = this.contract.sections[ev.from.id.replace('contracts_sections_', '')]
      const toContents   = new_section.contents.map((s, i) => ({ id: s.id, order: i + 1, contract_section_id: new_section.id }))
      const fromContents = old_section.contents.map((s, i) => ({ id: s.id, order: i + 1, contract_section_id: old_section.id }))
      const result = await updateContractSectionContentOrders({ contents: [...toContents, ...fromContents] })
      if (result.code === 200) this.getContractInfo()
    },
    startEndDragNewCategory: _debounce(function (ev) { this.endDragNewCategory(ev) }, 800),
    async endDragNewCategory(ev) {
      const section = this.companyContractCategories[ev.oldIndex]
      await createContractSectionProject({ contract_id: this.contract.id, title: section.title, section_type: section.section_type, order: ev.newIndex + 1, company_planning_cat_id: section.id })
      this.getContractInfo()
    },
    startEndDragNewContent: _debounce(function (ev) { this.endDragNewContent(ev) }, 800),
    async endDragNewContent(ev) {
      const section = this.contract.sections[ev.to.id.replace('contracts_sections_', '')]
      const content = this.$store.state.contracts.companyContracts[ev.oldIndex]
      await createContractSectionContent({ title: content.title, content: content.content, contract_section_id: section.id, company_contract_section_id: content.id, order: ev.newIndex + 1 })
      this.getContractInfo()
    },

    // ── Comentarios / Observaciones ──────────────────────────────────────

    openCommentSection(section_content_id) {
      this.menuComment = section_content_id
      this.$nextTick(() => {
        const buttonRef = this.$refs[`commentActivatorButton_${section_content_id}`]
        if (!buttonRef) return
        const buttonEl  = Array.isArray(buttonRef) ? buttonRef[0] : buttonRef
        const rect      = buttonEl.$el.getBoundingClientRect()
        const cardWidth = 400, cardHeight = 500
        let top  = rect.bottom + window.scrollY + 5
        let left = rect.left   + window.scrollX
        if (left + cardWidth  > window.innerWidth)               left = window.innerWidth  - cardWidth  - 10
        if (top  + cardHeight > window.innerHeight + window.scrollY) top = window.innerHeight + window.scrollY - cardHeight - 10
        this.commentPosition = { top, left, width: rect.width }
      })
      this.markAsReadNotification(section_content_id)
    },
    closeCommentForm() { this.menuComment = null },
    markAsReadNotification(section_content_id) {
      this.markAsReadRTDBNotifications(section_content_id)
      const ids = this.$store.state.notifications.contractNotifications
        .filter(item => !item.readed && item.data?.contract_content_id === section_content_id)
        .map(item => item.id)
      ids.forEach(id => this.markNotificationAsRead(id))
    },
    markAsReadRTDBNotifications(section_content_id) {
      eventBus.$emit('deleteRTDBNotifications', { type: 'contracts', keys_to_remove: this.getContractKeysByContentId(section_content_id) })
    },
    async markNotificationAsRead(notification_id) {
      const result = await markAsReaded({ id: notification_id })
      if (result.code === 200) this.$store.dispatch('notifications/getNotifications', { take: 10, types: [4] })
    },
    countContractsByContentId(id) {
      const contracts = this.$store.state.notifications.rtdbNotifications?.contracts
      if (!contracts) return 0
      return Object.values(contracts).reduce((n, c) => c.data?.contract_content_id === id ? n + 1 : n, 0)
    },
    getContractKeysByContentId(id) {
      const contracts = this.$store.state.notifications.rtdbNotifications?.contracts
      if (!contracts) return []
      return Object.keys(contracts).filter(k => contracts[k].data?.contract_content_id === id)
    },

    // ── Responsable y datos del proyecto ────────────────────────────────

    changeResponsible: _debounce(function(responsible_id) { this._changeResponsible(responsible_id) }, 800),
    async _changeResponsible(responsible_id) {
      const type = MORPHABLE_TYPE_MAP[this.contract.morphable_type] ?? 'Otro'
      const handlerMap = {
        crm:        () => editCrmResponsible({ conversation_id: this.contract.morphable_id, responsible_id }),
        preproject: () => setResponsiblePreProject({ preproject_id: this.contract.morphable_id, responsible_id }),
        project:    () => setResponsibleProject({ project_id: this.contract.morphable_id, responsible_id, from_state: this.responsableFromState, contract_id: this.contract.id }),
      }
      await handlerMap[type]?.()
      this.getContractInfo()
      this.responsibleMenu = false
    },

    getAssociationType(type) { return MORPHABLE_LABEL_MAP[type] ?? 'Otro' },

    async changeProjectType(project_type_id) {
      const result = await updateContract({ contract_id: this.contract.id, project_type_id })
      if (result.code === 200) this.getContractInfo()
    },
    changeProjectDestiny(destiny_id) {
      updateProjectInfo({ project_id: this.project.id, destiny_id }).then(r => { if (r.code === 200) this.getContractInfo() })
    },
    editProjectData() {
      this.$store.commit('projects/setEditProjectModal', { ...this.contract.morphable, parent_type: 'contract', parent_id: this.contract.id })
    },
    editUser() {
      this.$store.commit('users/setNewUserModalEditing', { ...this.contract.morphable?.client, user_id: this.contract.morphable?.client?.id, contract_id: this.contract.id, client: true })
      this.$store.commit('users/setNewUserModal', true)
    },
    async changeIptZone(zone_id) {
      const result = await updateProjectInfo({ project_id: this.contract?.morphable?.id, zone_id })
      if (result.code === 200) this.getContractInfo()
    },
    handleGoTo(association) {
      const goToMap = {
        'App\\Models\\PreProject':      () => this.$store.commit('preprojects/setPreProjectModalId', association.morphable_id),
        'App\\Models\\BotConversation': () => this.$store.commit('crm/setCrmModalId', association.morphable_id),
      }
      goToMap[association.morphable_type]?.()
    },

    // ── PDF y firma ──────────────────────────────────────────────────────

    async downloadPDF() {
      this.loadingPdf = true
      const pdf = await createContractPDF({ contract_id: this.contract.id })
      if (pdf.code === 200) window.open(pdf.url)
      this.loadingPdf = false
    },
    async contractToPdf() {
      const ref  = this.$store.state.contracts.contractEditorRef
      const data = { contract_id: this.contract.id, contents: JSON.stringify(ref.pages.map(p => this.reverseContentViewToPlaceholders(p.prev_innerHTML))) }
      const pdf  = await createContractPdfFromPreview(data)
      if (pdf.code === 200) {
        this.getContractInfo()
        this.$store.commit('contracts/setContractPdfModal', { ...this.contract, pdf: pdf.contract_pdf })
				// this.openContractPDF()
      }
    },
    async companyRepSign() {
      this.$store.commit('contracts/setContractSignModal', { ...this.contract, companySign: true })
    },
    async sendEmail() {
      if (!this.contract.pdf) return
      try {
        await sendContractPdfEmail({ contract_id: this.contract.id })
        this.getContractInfo()
        this.setMessage({ message: 'Se ha enviado correctamente el correo al cliente', title: 'Correo enviado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
      } catch {
        this.setMessage({ message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
      }
    },

    // ── Pagos ────────────────────────────────────────────────────────────

    changePaymentRequired() {
      this.contractPaymentRequired = !this.contractPaymentRequired
      this.contractValueMenu       = false
      this.debouncedPersistPaymentRequired()
    },
    debouncedPersistPaymentRequired: _debounce(function () { this.persistPaymentRequired() }, 800),
    async persistPaymentRequired() {
      await updateContract({ contract_id: this.contract.id, payment_required: this.contractPaymentRequired })
    },
    async sendPaymentRequestEmail(contractValue) {
      try {
        this.contractValue     = contractValue
        this.contractValueMenu = false
        await sendContractPaymentRequest({ contract_id: this.contract.id, contract_value: this.contractValue })
        this.getContractInfo()
        this.setMessage({ message: 'Se ha enviado correctamente el correo al cliente', title: 'Correo enviado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
      } catch {
        this.setMessage({ message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
      }
    },

    // ── Pagos (listener para botón inyectado en el HTML del contrato) ───

    setupPaymentsButtonListener() {
      const observer = new MutationObserver(() => {
        const btn = document.getElementById('edit-payments-btn')
        if (btn) {
					btn.addEventListener('click', this.editProjectPlanning);
					// observer.disconnect();
				}
        const btn_sub = document.getElementById('edit-subcontract-payments-btn')
        if (btn_sub) {
					btn_sub.addEventListener('click', this.editSubcontractProjectPlanning);
					// observer.disconnect();
				}
      })
      observer.observe(document.body, { childList: true, subtree: true })
    },
    editProjectPlanning() {
      this.$store.commit('costbreakdown/setCostBreakdownSummaryTablesModal', true)
      this.$store.commit('costbreakdown/setCostBreakdownSummaryTablesModalInfo', { showPaymentConfig: true })
      this.$emit('open-cost-breakdown-modal')
    },
		editSubcontractProjectPlanning(){
			this.$store.commit('costbreakdown/setCostBreakdownSubcontractTablesModal', true)
		},

    // ── Prefijo ──────────────────────────────────────────────────────────

    async changeContractPrefix(contract_prefix_id) {
      await updateContract({ contract_id: this.contract.id, contract_prefix_id })
      this.getContractInfo()
      this.prefixMenu = false
    },

    // ── Anexos ───────────────────────────────────────────────────────────

    async startContractAddendum() {
      const confirmed = await confirm('¿Estás seguro de que deseas crear un nuevo anexo para este contrato?')
      if (confirmed) this.createContractAddendum()
    },
    async createContractAddendum() {
      const parentId = this.parentContractIdGetter ? this.parentContractIdGetter(this.contract) : this.contract.id
      const addendum = await addContractAddendum({ contract_id: parentId })
      if (addendum.code === 200) {
        if (addendum.addendum_notsigned) {
          this.setMessage({ message: 'Aún hay anexos pendientes de revisión, antes de iniciar un nuevo anexo todos los anteriores deben haber sido firmados o cancelados.', title: 'Anexos sin finalizar', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
          return
        }
        this.$router.push({ name: 'ContractAddendumDetail', query: { id: addendum.contract_addendum.id } })
        this.setMessage({ message: 'Se ha creado correctamente el anexo.', title: 'Anexo Creado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
      } else {
        this.setMessage({ message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
      }
    },
    toggleDeletedAddendumShow() { this.deletedAddendumShow = !this.deletedAddendumShow },

    // ── Eliminar anexo (solo addendum) ───────────────────────────────────

    async startDeleteContract() {
      this.$emit('start-delete-contract')
    },

    // ── Templates ────────────────────────────────────────────────────────

    async templateContentSelected(template) {
      this.loading = true
      await setTemplateToContract({ contract_id: this.contract.id, template_content: template })
      this.getContractInfo()
      this.loading = false
    },
    async onChangeTemplate(template) {
      this.loading = true; this.menuTemplate = false; this.menuTemplates = false
      await setTemplateToContract({ contract_id: this.contract.id, contract_template_id: template.id })
      this.getContractInfo(); this.loading = false
    },
    async saveTemplate() {
      await createContractTemplate({ contract_id: this.contract.id, title: this.templateName })
      this.$store.dispatch('contracts/getContractTemplates', { start: 0, limit: 100 })
      this.closeTemplateMenu()
    },
    async deleteTemplate(template) {
      await deleteContractTemplate({ contract_template_id: template.id })
      this.$store.dispatch('contracts/getContractTemplates', { start: 0, limit: 100 })
    },
    closeTemplateMenu() { this.menuTemplate = false; this.templateName = null },

    // ── Títulos ──────────────────────────────────────────────────────────

    async saveTitle() {
      const result = await createContractTitle({ contract_title: this.contractTitle })
      this.closeTitleMenu()
      this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 })
      this.setMessage(result.code === 200
        ? { message: 'Se ha creado correctamente el título', title: 'Título Creado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' }
        : { message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
    },
    async saveSubtitle() {
      const result = await createContractSubtitle({ contract_subtitle: this.contractSubtitle })
      this.closeTitleMenu()
      this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 })
      this.setMessage(result.code === 200
        ? { message: 'Se ha creado correctamente el subtítulo', title: 'Subtítulo Creado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' }
        : { message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
    },
    async updateTitle(t) {
      await updateContractTitle({ contract_title_id: t.id, contract_title: t.title })
      this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 })
      this.edditingTitle = { id: null, title: null }
    },
    async updateSubtitle(s) {
      await updateContractSubtitle({ contract_subtitle: s.subtitle, contract_subtitle_id: s.id })
      this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 })
      this.edditingSubtitle = { id: null, subtitle: null }
    },
    async deleteTitle(id) { await deleteContractTitle({ contract_title_id: id }); this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 }) },
    async deleteSubtitle(id) { await deleteContractSubtitle({ contract_subtitle_id: id }); this.$store.dispatch('contracts/getContractTitles', { start: 0, limit: 100 }) },
    closeTitleMenu() {
      this.menuAddTitle = false; this.contractTitle = null; this.contractSubtitle = null
      this.$refs.formTitle?.[0]?.resetValidation()
      this.$refs.formSubtitle?.[0]?.resetValidation()
    },
    closeSubtitleMenu() { this.menuAddSubtitle = false; this.contractSubtitle = null },
    onChangeTitle(title)    { this.newSection.title    = title.title },
    onChangeSubtitle(title) { this.newSection.subtitle = title.subtitle },

    // ── Scroll y navegación ──────────────────────────────────────────────

    scrollToSection() {
      this.$nextTick(() => {
        if (!this.$route?.hash) return
        this.contractPreviewing = false
        this.$nextTick(() => {
          const section         = document.querySelector(this.$route.hash)
          const scrollContainer = this.$refs.colContractContainer
          window.history.replaceState(null, null, window.location.pathname + window.location.search)
          if (section && scrollContainer) {
            const sectionTop = section.offsetTop - scrollContainer.offsetTop
            scrollContainer.scrollTo({ top: sectionTop - 100, behavior: 'smooth' })
            setTimeout(() => {
              this.checkForHash()
              if (this.$route) this.$router.replace({ query: { id: this.contract?.id | this.effectiveContractId } })
            }, 500)
          }
        })
      })
    },
    checkForHash() {
      if (this.$route?.hash?.startsWith('#section_content_id_')) {
        const id = this.$route.hash.replace('#section_content_id_', '')
        if (id) this.openCommentSection(id)
      }
    },
    onScroll(event) { this.$store.commit('contracts/setScrolledContract', event.target.scrollTop) },

    // ── ResizeObserver ───────────────────────────────────────────────────

    initResizeObserver() {
      const el = this.$refs.contractHeaderInfo?.$el || this.$refs.contractHeaderInfo
      if (!el) return
      this.resizeObserver?.disconnect()
      this.resizeObserver = new ResizeObserver(() => {
        this.$store.commit('contracts/setContractHeaderHeight', el.offsetHeight)
      })
      this.resizeObserver.observe(el)
    },

    // ── Secciones de empresa ─────────────────────────────────────────────

    editCompanySection() {
      window.history.replaceState(null, null, '#contract-only')
      this.$store.commit('companies/setCompanyContractConfigModal', !this.$store.state.companies.companyContractConfigModal)
    },
    onClickCategory(data) { this.selectedCategory = data },
    isCompanyFirstSection(item) {
      if (this.selectedCategory?.section_type !== 1) return true
      const billingInfo    = this.contract?.morphable?.client?.company_profile
      const accreditation  = this.contract?.morphable?.client?.accreditation
      const hasCompanyName = item.content.includes('client_company_name')
      return billingInfo?.work_as_company && accreditation?.status ? hasCompanyName : !hasCompanyName
    },

    // ── Transformaciones de contenido ────────────────────────────────────

    transformPlaceholdersToFriendlyView(content) {
      let result = content
      for (const [placeholder, label] of Object.entries(this.placeholderMapping)) {
        result = result.replace(
          new RegExp(placeholder, 'g'),
          `<span class="friendly-placeholder" style="font-size:12px;padding:1px 6px;border:1px solid grey;border-radius:5px;background:white;color:orange;" contenteditable="false">${label}</span>`
        )
      }
      return result
    },
    transformPlaceholdersToContentView(content)  { return transformPlaceholdersToRealContent(this.contract, content, !this.showingObservations && !this.isClient) },
    reverseContentViewToPlaceholders(content)    { return reverseContentToPlaceholders(content) },

    // ── Notificaciones de recarga ────────────────────────────────────────

    _onReloadInformation(data) {
      if (!this.contract) return
      this.contractPreviewing        = false
      this.loadingContractPreviewing = false
      this.getContractInfo()
      if (this.menuComment && data && this.menuComment === data.contract_content_id) {
        this.markAsReadNotification(data.contract_content_id)
      }
    },

    // ── Misc ────────────────────────────────────────────────────────────

    setMessage(messageData) {
      this.$store.dispatch('general/addMessage', { ...messageData, created: new Date() })
    },
    getModificationValue(currency) {
      let total = 0
      this.contract?.morphable?.costs?.stages?.forEach(({ stage, items }) => {
        if (stage?.is_modification) {
          items.forEach(item => {
            total += currency === 1 ? (item.qty || 0) * (item.unit_cost_uf || 0) : (item.qty || 0) * (item.unit_cost_clp || 0)
          })
        }
      })
      return currency === 1 ? `${this.$options.filters.toThousandFilter(total, 2)} UF` : `${this.$options.filters.toThousandFilter(total)} CLP`
    },
    toProjectTable() {
      window.open(`/built-in-content?type=contract&id=${this.contract.id}`, '', 'width=1000,height=700,toolbar=no,menubar=no,location=no,status=no')
      this.$router.push({ name: 'ProjectCostBreakdown', params: { id: this.contract.morphable?.id }, hash: '#summary' })
    },
    isNumberKey(event) {
      const charCode = event.which ? event.which : event.keyCode
      if (charCode < 48 || charCode > 57) event.preventDefault()
    },
    contractStatusColor(s) { return this.$options.filters.contractStatusColor(s) },
  },
}
</script>

<style scoped>
.contract-container { background-color: white; text-transform: none; }
.section-container  { border: 1px solid #d3d3d3; }
.hover-section-content {
  position: absolute; top: 20px; left: 10px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #cecece; border-radius: 4px;
  border-top-left-radius: 0; border-bottom-left-radius: 0; border-left: none;
}
.hover-section-content::before {
  content: ""; position: absolute; top: -20px; left: -11px;
  width: 20px; height: 20px;
  border: 1px solid #cecece; border-top: none; border-right: none; border-bottom-left-radius: 10px;
}
.hover-section-content::after {
  content: ""; position: absolute; bottom: -20px; left: -11px;
  width: 20px; height: 20px;
  border: 1px solid #cecece; border-bottom: none; border-right: none; border-top-left-radius: 10px;
}
.section-content-container {
  position: relative; text-transform: none;
  border: 1px solid #cecece; border-radius: 6px;
}
.section-content-container:hover { background: #fbfbfb; }
/deep/ .section-content-container p,
/deep/ .section-content-container div,
/deep/ .section-content-container ul,
/deep/ .section-content-container span,
/deep/ .section-content-container { text-transform: none; }
/deep/ .section-content-container img { max-width: 100%; object-fit: contain; }
.custom-list-drag  { background: white; border: 1px solid #d3d3d3; cursor: pointer; }
.custom-list-drag.selected { background: #d3d3d3; }
.draggable-contents { max-height: 800px; overflow: scroll; }
.action-container  { width: 60px; display: flex; align-items: center; justify-content: flex-end; }
.add-comment-icon  { position: absolute; bottom: 0; right: 0; margin: auto; background: white; z-index: 9999; }
.card-comment-container { position: absolute; top: 0; right: 60px; background: white; z-index: 9999; }
@keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
.pulse-animation { animation: pulse 1.5s infinite; }
/deep/ .section-content p { margin-bottom: 0; }
</style>